import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShoppingCart, Plus, Trash2, Check, Circle, Eraser, Sparkles } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const CompraTab = ({ headers }) => {
  const [items, setItems] = useState([]);
  const [nombre, setNombre] = useState('');
  const [sugerencias, setSugerencias] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/familia/compra`, { headers });
      setItems(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  // Autocomplete con debounce
  useEffect(() => {
    if (!nombre.trim()) { setSugerencias([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/familia/compra/sugerencias`, { headers, params: { q: nombre.trim() } });
        setSugerencias(data);
      } catch (e) { console.error(e); }
    }, 250);
    return () => clearTimeout(t);
  }, [nombre]); // eslint-disable-line react-hooks/exhaustive-deps

  const addItem = async (nombreItem) => {
    const n = (nombreItem || nombre).trim();
    if (!n) return;
    setNombre('');
    setSugerencias([]);
    try {
      await axios.post(`${API}/familia/compra`, { nombre: n }, { headers });
      load();
    } catch (e) { toast.error('No se pudo añadir'); }
  };

  const toggleItem = async (id) => {
    try {
      await axios.post(`${API}/familia/compra/${id}/toggle`, {}, { headers });
      load();
    } catch (e) { console.error(e); }
  };

  const deleteItem = async (id) => {
    try {
      await axios.delete(`${API}/familia/compra/${id}`, { headers });
      load();
    } catch (e) { console.error(e); }
  };

  const limpiarComprados = async () => {
    try {
      const { data } = await axios.post(`${API}/familia/compra/limpiar`, {}, { headers });
      toast.success(`${data.eliminados} producto${data.eliminados === 1 ? '' : 's'} eliminado${data.eliminados === 1 ? '' : 's'}`);
      load();
    } catch (e) { console.error(e); }
  };

  const pendientes = items.filter(i => !i.comprado);
  const comprados = items.filter(i => i.comprado);

  return (
    <div className="px-4 pb-24" data-testid="compra-tab">
      {/* Input con autocompletado */}
      <div className="relative mb-4">
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <ShoppingCart className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-amber-400/70" />
            <input
              value={nombre}
              onChange={e => setNombre(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addItem()}
              placeholder="Añadir producto..."
              className="w-full bg-gray-900/80 border border-gray-800 rounded-xl pl-9 pr-3 py-2.5 text-sm text-white placeholder-gray-500 focus:border-amber-500/50 focus:outline-none"
              data-testid="compra-input"
            />
          </div>
          <button
            onClick={() => addItem()}
            disabled={!nombre.trim()}
            className="px-4 rounded-xl bg-amber-500/20 border border-amber-500/40 text-amber-300 disabled:opacity-40 transition-opacity"
            data-testid="compra-add-btn"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>

        {/* Dropdown de sugerencias */}
        {sugerencias.length > 0 && (
          <div className="absolute z-20 left-0 right-12 mt-1 bg-gray-900 border border-gray-700 rounded-xl overflow-hidden shadow-2xl" data-testid="compra-sugerencias">
            {sugerencias.map(s => (
              <button
                key={s.nombre}
                onClick={() => addItem(s.nombre)}
                className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-gray-200 hover:bg-amber-500/10 transition-colors"
                data-testid={`sugerencia-${s.nombre}`}
              >
                {s.origen === 'historial'
                  ? <Sparkles className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />
                  : <ShoppingCart className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />}
                <span className="flex-1">{s.nombre}</span>
                {s.origen === 'historial' && <span className="text-[9px] text-amber-400/70 uppercase">habitual</span>}
              </button>
            ))}
          </div>
        )}
      </div>

      {loading ? (
        <p className="text-gray-500 text-sm text-center py-8">Cargando...</p>
      ) : items.length === 0 ? (
        <div className="text-center py-10">
          <ShoppingCart className="w-10 h-10 text-gray-700 mx-auto mb-2" />
          <p className="text-sm text-gray-500">La lista está vacía. Añade el primer producto.</p>
        </div>
      ) : (
        <>
          {/* Pendientes */}
          <div className="space-y-2">
            {pendientes.map(item => (
              <div key={item.id} className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-gray-900/60 border border-gray-800" data-testid={`compra-item-${item.id}`}>
                <button onClick={() => toggleItem(item.id)} className="flex-shrink-0" data-testid={`compra-toggle-${item.id}`}>
                  <Circle className="w-5 h-5 text-gray-600 hover:text-amber-400 transition-colors" />
                </button>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white font-medium">{item.nombre}</p>
                  {item.added_by_nombre && <p className="text-[9px] text-gray-500">añadido por {item.added_by_nombre}</p>}
                </div>
                <button onClick={() => deleteItem(item.id)} className="text-gray-600 hover:text-red-400 transition-colors" data-testid={`compra-delete-${item.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

          {/* Comprados (tachados, sin borrar) */}
          {comprados.length > 0 && (
            <div className="mt-5">
              <div className="flex items-center justify-between mb-2 px-1">
                <span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">
                  En el carro · {comprados.length}
                </span>
                <button
                  onClick={limpiarComprados}
                  className="flex items-center gap-1 text-[11px] text-amber-400 hover:text-amber-300 font-semibold"
                  data-testid="compra-limpiar-btn"
                >
                  <Eraser className="w-3.5 h-3.5" /> Limpiar comprados
                </button>
              </div>
              <div className="space-y-1.5">
                {comprados.map(item => (
                  <div key={item.id} className="flex items-center gap-3 px-3 py-2 rounded-xl bg-gray-900/30 border border-gray-800/50 opacity-60" data-testid={`compra-item-${item.id}`}>
                    <button onClick={() => toggleItem(item.id)} className="flex-shrink-0 w-5 h-5 rounded-full bg-emerald-500/20 border border-emerald-500/50 flex items-center justify-center" data-testid={`compra-toggle-${item.id}`}>
                      <Check className="w-3 h-3 text-emerald-400" />
                    </button>
                    <p className="flex-1 text-sm text-gray-400 line-through">{item.nombre}</p>
                    <button onClick={() => deleteItem(item.id)} className="text-gray-700 hover:text-red-400 transition-colors" data-testid={`compra-delete-${item.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
